// components/NotFoundPage.js
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { keyframes } from '@emotion/react';
import { Code } from '@mui/icons-material';

// Custom animations
const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const glowAnimation = keyframes`
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const shakeAnimation = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
`;

const NotFoundPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 50%, #2a1f3d 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        pt: 8, // Account for header height
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.3) 0%, transparent 70%)',
          animation: `${floatAnimation} 6s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 77, 77, 0.2) 0%, transparent 70%)',
          animation: `${floatAnimation} 8s ease-in-out infinite 1s`,
          opacity: 0.4,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '20%',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.2) 0%, transparent 70%)',
          animation: `${floatAnimation} 7s ease-in-out infinite 0.5s`,
          opacity: 0.5,
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          {/* Left Column - Illustration */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              {/* Main 404 Illustration */}
              <Box
                sx={{
                  position: 'relative',
                  textAlign: 'center',
                  mb: 4,
                }}
              >
                {/* Animated Number 4 */}
                <Box
                  sx={{
                    display: 'inline-block',
                    position: 'relative',
                    animation: `${shakeAnimation} 5s ease-in-out infinite`,
                  }}
                >
                  <Typography
                    variant="h1"
                    component="span"
                    sx={{
                      fontSize: { xs: '8rem', md: '12rem' },
                      fontWeight: 'bold',
                      background: 'linear-gradient(45deg, #8b0000, #ff4d4d, #8b0000)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      backgroundSize: '200% 200%',
                      animation: `${glowAnimation} 3s ease-in-out infinite`,
                      textShadow: '0 0 30px rgba(139, 0, 0, 0.5)',
                    }}
                  >
                    4
                  </Typography>
                </Box>

                {/* Central Icon */}
                <Box
                  sx={{
                    display: 'inline-block',
                    mx: 2,
                    animation: `${floatAnimation} 4s ease-in-out infinite`,
                  }}
                >
                  <SearchOffIcon
                    sx={{
                      fontSize: { xs: '6rem', md: '8rem' },
                      color: '#ff4d4d',
                      filter: 'drop-shadow(0 0 20px rgba(255, 77, 77, 0.5))',
                    }}
                  />
                </Box>

                {/* Second Animated Number 4 */}
                <Box
                  sx={{
                    display: 'inline-block',
                    position: 'relative',
                    animation: `${shakeAnimation} 5s ease-in-out infinite 0.5s`,
                  }}
                >
                  <Typography
                    variant="h1"
                    component="span"
                    sx={{
                      fontSize: { xs: '8rem', md: '12rem' },
                      fontWeight: 'bold',
                      background: 'linear-gradient(45deg, #ff4d4d, #8b0000, #ff4d4d)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      backgroundSize: '200% 200%',
                      animation: `${glowAnimation} 3s ease-in-out infinite 0.5s`,
                      textShadow: '0 0 30px rgba(255, 77, 77, 0.5)',
                    }}
                  >
                    4
                  </Typography>
                </Box>

                {/* Warning Icon Floating */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-20px',
                    right: '30%',
                    animation: `${floatAnimation} 3s ease-in-out infinite 0.2s`,
                  }}
                >
                  <WarningAmberIcon
                    sx={{
                      fontSize: '2.5rem',
                      color: '#ffd700',
                      filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))',
                    }}
                  />
                </Box>
              </Box>

              {/* Error Message Card */}
              <Paper
                elevation={8}
                sx={{
                  background: 'linear-gradient(135deg, rgba(10, 17, 40, 0.9) 0%, rgba(26, 31, 61, 0.9) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(139, 0, 0, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  textAlign: 'center',
                  maxWidth: 400,
                  width: '100%',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff, #ff4d4d)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  Page Not Found
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 3,
                    lineHeight: 1.6,
                  }}
                >
                  Oops! The page you're looking for seems to have wandered off into the digital void. 
                  It might have been moved, deleted, or never existed in the first place.
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontStyle: 'italic',
                    mb: 3,
                  }}
                >
                  Error 404: Resource not found
                </Typography>
              </Paper>
            </Box>
          </Grid>

          {/* Right Column - Action Buttons & Help Text */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
                gap: 3,
              }}
            >
              {/* Main Actions */}
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    mb: 3,
                  }}
                >
                  Let's Get You Back on Track
                </Typography>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mb: 4,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <Button
                    component={Link}
                    to="/"
                    variant="contained"
                    size="large"
                    startIcon={<HomeIcon />}
                    sx={{
                      background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      minWidth: 200,
                      boxShadow: '0 8px 25px rgba(139, 0, 0, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #ff4d4d, #8b0000)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 35px rgba(139, 0, 0, 0.6)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Go Home
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => window.history.back()}
                    sx={{
                      borderColor: '#ff4d4d',
                      color: '#ff4d4d',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      minWidth: 200,
                      '&:hover': {
                        borderColor: '#ff4d4d',
                        background: 'rgba(139, 0, 0, 0.1)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(139, 0, 0, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Go Back
                  </Button>
                </Box>
              </Box>

              {/* Help Section */}
              <Paper
                elevation={4}
                sx={{
                  background: 'linear-gradient(135deg, rgba(139, 0, 0, 0.1) 0%, rgba(26, 31, 61, 0.8) 100%)',
                  border: '1px solid rgba(139, 0, 0, 0.2)',
                  borderRadius: 3,
                  p: 3,
                  maxWidth: 400,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: '#ff4d4d',
                    fontWeight: 'bold',
                    mb: 2,
                  }}
                >
                  Need Help?
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 2,
                  }}
                >
                  If you believe this is an error or need assistance, here are some options:
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    pl: 2,
                    '& li': {
                      mb: 1,
                    },
                  }}
                >
                  <li>Check the URL for typos</li>
                  <li>Visit our homepage to navigate</li>
                  <li>Contact our support team</li>
                  <li>Use the search function</li>
                </Box>
              </Paper>

              {/* Contact Support */}
              <Box
                sx={{
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Button
                  component={Link}
                  to="/contact"
                  variant="text"
                  sx={{
                    color: '#ff4d4d',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      color: '#ff6b6b',
                      background: 'rgba(139, 0, 0, 0.1)',
                    },
                  }}
                >
                  Contact Support →
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Floating Elements */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '5%',
            left: '5%',
            opacity: 0.1,
            animation: `${floatAnimation} 10s ease-in-out infinite`,
          }}
        >
          <Code sx={{ fontSize: '4rem', color: '#ff4d4d' }} />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            opacity: 0.1,
            animation: `${floatAnimation} 12s ease-in-out infinite 1s`,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: '3rem', color: '#ff4d4d' }} />
        </Box>
      </Container>
    </Box>
  );
};

export default NotFoundPage;